import { Component, OnInit, OnDestroy, HostListener, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { RoomDisplay } from '../../models/room.model';
import { RoomDetailModalComponent } from '../../shared/components/room-detail-modal/room-detail-modal.component';
import { HotelGalleryModalComponent, GalleryImage } from '../../shared/components/hotel-gallery-modal/hotel-gallery-modal.component';

// Interface para opciones del selector de habitaciones (basado en OpenAPI)
interface RoomTypeOption {
  id: number;
  name: string;        // nombre del OpenAPI
  priceMin: number;
  priceMax: number;
  precioPromedio: number;
  capacidad: number;   // capacidad del OpenAPI (1-10 personas)
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, FormsModule, RoomDetailModalComponent, HotelGalleryModalComponent],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss', './booking-widget-styles.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  hotelName = 'SUNTUR QURI WASI';
  currentYear = new Date().getFullYear();
  isScrolled = false;
  isLoadingRooms = true;

  // Modal State
  selectedRoomForModal = signal<RoomDisplay | null>(null);

  openRoomDetail(room: RoomDisplay) {
    this.selectedRoomForModal.set(room);
    this.pauseAutoplay();
    document.body.style.overflow = 'hidden'; // Prevent scroll
  }

  closeRoomDetail() {
    this.selectedRoomForModal.set(null);
    this.resumeAutoplay();
    document.body.style.overflow = ''; // Restore scroll
  }

  // Hotel Gallery State
  isHotelGalleryOpen = signal(false);
  hotelGalleryImages = signal<GalleryImage[]>([]);

  openHotelGallery() {
    if (this.hotelGalleryImages().length === 0) {
      this.apiService.getHotelGallery().subscribe(images => {
        this.hotelGalleryImages.set(images);
      });
    }
    this.isHotelGalleryOpen.set(true);
    this.pauseAutoplay();
  }

  closeHotelGallery() {
    this.isHotelGalleryOpen.set(false);
    this.resumeAutoplay();
  }

  // Navigation items
  navItems = [
    { label: 'Inicio', id: 'home' },
    { label: 'Nosotros', id: 'sobre-nosotros' },
    { label: 'Habitaciones', id: 'habitaciones' },
    { label: 'Servicios', id: 'caracteristicas' },
    { label: 'Descubre', id: 'descubre' },
    { label: 'Cómo Llegar', id: 'ubicacion' }
  ];

  // Mobile menu state
  mobileMenuOpen = false;

  // Image paths
  logoPath = '/logos/logo_final.png';
  heroImagePath = '/Fotos/hotel-andino.jpg';

  // Rooms/Accommodations - Inicialmente vacío, se carga desde la API
  rooms: RoomDisplay[] = [];

  // Carousel properties
  currentSlide = 0;
  slideWidth = 33.333; // Para mostrar 3 cards en desktop
  cardsPerView = 3;

  // Autoplay properties
  isAutoplayActive = true;
  autoplayInterval = 5000; // 5 segundos
  private autoplayTimer: any = null;

  // Touch/Swipe properties
  private touchStartX = 0;
  private touchEndX = 0;
  private minSwipeDistance = 50;

  // Tablas de Sarhua carousel
  tablasImages = [
    { src: '/Fotos/tablas/tabla_1.jfif', alt: 'Tabla de Sarhua 1' },
    { src: '/Fotos/tablas/tabla_2.jfif', alt: 'Tabla de Sarhua 2' },
    { src: '/Fotos/tablas/tabla_3.jpg', alt: 'Tabla de Sarhua 3' },
    { src: '/Fotos/tablas/tabla_4.jpg', alt: 'Tabla de Sarhua 4' },
    { src: '/Fotos/tablas/tabla_5.jpg', alt: 'Tabla de Sarhua 5' },
  ];
  currentTablaIndex = 0;
  private tablasAutoplayTimer: any = null;
  private tablasAutoplayInterval = 6000;

  // Gastronomía carousel
  gastroImages = [
    { src: '/Fotos/gastronomia/plato_tipico_1.jpg', alt: 'Plato típico 1' },
    { src: '/Fotos/gastronomia/plato_tipico_2.jpg', alt: 'Plato típico 2' },
    { src: '/Fotos/gastronomia/plato_tipico_3.jpg', alt: 'Plato típico 3' },
  ];
  currentGastroIndex = 0;
  private gastroAutoplayTimer: any = null;
  private gastroAutoplayInterval = 6000;

  // Festividad carousel
  festImages = [
    { src: '/Fotos/festividad/festividad_1.avif', alt: 'Festividad de Sarhua 1' },
    { src: '/Fotos/festividad/festividad_2.webp', alt: 'Festividad de Sarhua 2' },
  ];
  currentFestIndex = 0;
  private festAutoplayTimer: any = null;
  private festAutoplayInterval = 6000;

  // Tipos de habitación para el selector del widget
  roomTypeOptions: RoomTypeOption[] = [];


  // Features data con identificadores de íconos SVG
  features = [
    {
      iconId: 'mountain',
      title: 'Vista Andina',
      description: 'Disfruta de impresionantes vistas de la cordillera andina desde nuestras habitaciones'
    },
    {
      iconId: 'art',
      title: 'Arte Cultural',
      description: 'Sumérgete en la rica herencia artística de Sarhua, reflejada en cada rincón'
    },
    {
      iconId: 'food',
      title: 'Gastronomía Local',
      description: 'Saborea auténticos platos tradicionales preparados con productos de la región'
    },
    {
      iconId: 'comfort',
      title: 'Confort Moderno',
      description: 'Habitaciones equipadas con todas las comodidades para tu descanso perfecto'
    },
    {
      iconId: 'nature',
      title: 'Naturaleza Pura',
      description: 'Conecta con la naturaleza en un entorno de paz y tranquilidad absoluta'
    },
    {
      iconId: 'family',
      title: 'Atención Familiar',
      description: 'Servicio cálido y personalizado que te hará sentir como en casa'
    }
  ];

  // Gallery images (Single featured entry for the modal)
  galleryImages = [
    {
      src: '/Fotos/pueblo con encanto.webp',
      alt: 'Galería del Hotel'
    }
  ];

  // Booking widget properties
  checkInDate: string = '';
  checkOutDate: string = '';
  selectedGuests: string = '2';
  selectedRoomType: string = '';
  selectedRoomQuantity: number = 1;

  // Opciones para el widget de reserva
  guestOptions: { value: string; label: string }[] = [
    { value: '1', label: '1 Adulto' },
    { value: '2', label: '2 Adultos' },
    { value: '3', label: '3 Adultos' },
    { value: '4', label: '4 Adultos' },
    { value: '5', label: '5+ Adultos' }
  ];

  quantityOptions: number[] = [1, 2, 3, 4, 5];

  constructor(private apiService: ApiService) { }

  ngOnInit(): void {
    // Initial scroll check
    this.checkScroll();
    // Set minimum date for check-in (today)
    this.setMinDates();
    // Initialize carousel settings based on screen size
    this.updateCarouselSettings();
    // Load rooms from API
    this.loadRooms();
    // Load room types for booking widget
    this.loadRoomTypes();
    // Start tablas carousel autoplay
    this.startTablasAutoplay();
    // Start gastro carousel autoplay
    this.startGastroAutoplay();
    // Start festividad carousel autoplay
    this.startFestAutoplay();
  }

  ngOnDestroy(): void {
    this.stopTablasAutoplay();
    this.stopGastroAutoplay();
    this.stopFestAutoplay();
  }

  // Tablas carousel methods
  startTablasAutoplay(): void {
    this.stopTablasAutoplay();
    this.tablasAutoplayTimer = setInterval(() => {
      this.nextTabla();
    }, this.tablasAutoplayInterval);
  }

  stopTablasAutoplay(): void {
    if (this.tablasAutoplayTimer) {
      clearInterval(this.tablasAutoplayTimer);
      this.tablasAutoplayTimer = null;
    }
  }

  nextTabla(): void {
    this.currentTablaIndex = (this.currentTablaIndex + 1) % this.tablasImages.length;
  }

  prevTabla(): void {
    this.currentTablaIndex = (this.currentTablaIndex - 1 + this.tablasImages.length) % this.tablasImages.length;
  }

  goToTabla(index: number): void {
    this.currentTablaIndex = index;
    this.startTablasAutoplay();
  }

  // Gastronomía carousel methods
  startGastroAutoplay(): void {
    this.stopGastroAutoplay();
    this.gastroAutoplayTimer = setInterval(() => {
      this.nextGastro();
    }, this.gastroAutoplayInterval);
  }

  stopGastroAutoplay(): void {
    if (this.gastroAutoplayTimer) {
      clearInterval(this.gastroAutoplayTimer);
      this.gastroAutoplayTimer = null;
    }
  }

  nextGastro(): void {
    this.currentGastroIndex = (this.currentGastroIndex + 1) % this.gastroImages.length;
  }

  goToGastro(index: number): void {
    this.currentGastroIndex = index;
    this.startGastroAutoplay();
  }

  // Festividad carousel methods
  startFestAutoplay(): void {
    this.stopFestAutoplay();
    this.festAutoplayTimer = setInterval(() => {
      this.nextFest();
    }, this.festAutoplayInterval);
  }

  stopFestAutoplay(): void {
    if (this.festAutoplayTimer) {
      clearInterval(this.festAutoplayTimer);
      this.festAutoplayTimer = null;
    }
  }

  nextFest(): void {
    this.currentFestIndex = (this.currentFestIndex + 1) % this.festImages.length;
  }

  goToFest(index: number): void {
    this.currentFestIndex = index;
    this.startFestAutoplay();
  }

  /**
   * Establece las fechas mínimas para el widget de reserva
   */
  private setMinDates(): void {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Formato YYYY-MM-DD para inputs de tipo date
    this.checkInDate = this.formatDate(today);
    this.checkOutDate = this.formatDate(tomorrow);
  }

  /**
   * Formatea una fecha a YYYY-MM-DD
   */
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // ==================== CAROUSEL METHODS ====================

  /**
   * Calcula el índice máximo del slide (último slide posible)
   */
  get maxSlide(): number {
    return Math.max(0, this.rooms.length - 1);
  }

  /**
   * Avanza al siguiente slide del carrusel (loop infinito)
   */
  nextSlide(): void {
    if (this.currentSlide < this.maxSlide) {
      this.currentSlide++;
    } else {
      // Loop al inicio
      this.currentSlide = 0;
    }
    this.restartAutoplay();
  }

  /**
   * Retrocede al slide anterior del carrusel (loop infinito)
   */
  prevSlide(): void {
    if (this.currentSlide > 0) {
      this.currentSlide--;
    } else {
      // Loop al final
      this.currentSlide = this.maxSlide;
    }
    this.restartAutoplay();
  }

  /**
   * Navega a un slide específico
   */
  goToSlide(index: number): void {
    if (index >= 0 && index <= this.maxSlide && index !== this.currentSlide) {
      this.currentSlide = index;
      this.restartAutoplay();
    }
  }

  /**
   * Genera un array para los indicadores del carrusel
   */
  getCarouselDots(): number[] {
    return Array(this.maxSlide + 1).fill(0);
  }

  /**
   * Obtiene la transformación CSS para el carrusel
   */
  getCarouselTransform(): string {
    const offset = this.currentSlide * 100;
    return `translateX(-${offset}%)`;
  }

  /**
   * Ajusta el carrusel según el tamaño de pantalla
   */
  private updateCarouselSettings(): void {
    const width = window.innerWidth;
    if (width < 768) {
      this.cardsPerView = 1;
      this.slideWidth = 100;
    } else if (width < 1024) {
      this.cardsPerView = 1;
      this.slideWidth = 100;
    } else {
      this.cardsPerView = 1;
      this.slideWidth = 100;
    }
    // Asegurar que el slide actual sea válido
    if (this.currentSlide > this.maxSlide) {
      this.currentSlide = this.maxSlide;
    }
  }

  // ==================== AUTOPLAY METHODS ====================

  /**
   * Inicia el autoplay del carrusel
   */
  startAutoplay(): void {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
    }
    if (this.isAutoplayActive && this.rooms.length > 1) {
      this.autoplayTimer = setInterval(() => {
        this.nextSlide();
      }, this.autoplayInterval);
    }
  }

  /**
   * Detiene el autoplay temporalmente
   */
  pauseAutoplay(): void {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  /**
   * Reanuda el autoplay si está activo
   */
  resumeAutoplay(): void {
    if (this.isAutoplayActive) {
      this.startAutoplay();
    }
  }

  /**
   * Reinicia el temporizador del autoplay
   */
  restartAutoplay(): void {
    this.pauseAutoplay();
    this.resumeAutoplay();
  }

  /**
   * Alterna el estado del autoplay
   */
  toggleAutoplay(): void {
    this.isAutoplayActive = !this.isAutoplayActive;
    if (this.isAutoplayActive) {
      this.startAutoplay();
    } else {
      this.pauseAutoplay();
    }
  }

  // ==================== TOUCH/SWIPE METHODS ====================

  /**
   * Maneja el inicio del touch
   */
  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.touches[0].clientX;
  }

  /**
   * Maneja el movimiento del touch
   */
  onTouchMove(event: TouchEvent): void {
    this.touchEndX = event.touches[0].clientX;
  }

  /**
   * Maneja el fin del touch y determina la dirección del swipe
   */
  onTouchEnd(): void {
    const swipeDistance = this.touchStartX - this.touchEndX;

    if (Math.abs(swipeDistance) > this.minSwipeDistance) {
      if (swipeDistance > 0) {
        // Swipe izquierda - siguiente
        this.nextSlide();
      } else {
        // Swipe derecha - anterior
        this.prevSlide();
      }
    }

    // Reset valores
    this.touchStartX = 0;
    this.touchEndX = 0;
  }

  // ==================== END CAROUSEL METHODS ====================

  /**
   * Carga los tipos de habitación para el widget de reserva
   * Usa el endpoint GET /room-types del OpenAPI
   */
  private loadRoomTypes(): void {
    // Cargar tipos y habitaciones para obtener precios
    forkJoin({
      types: this.apiService.getRoomTypes(),
      rooms: this.apiService.getRooms()
    }).subscribe({
      next: ({ types, rooms }) => {
        if (types && types.length > 0) {
          // Crear mapa de precios por tipo de habitación
          const pricesByTypeMin = new Map<number, number>();
          const pricesByTypeMax = new Map<number, number>();
          rooms.forEach(room => {
            if (!pricesByTypeMin.has(room.tipoHabitacionId) || pricesByTypeMin.get(room.tipoHabitacionId)! > room.precioPorNoche) {
              pricesByTypeMin.set(room.tipoHabitacionId, room.precioPorNoche);
            }
            if (!pricesByTypeMax.has(room.tipoHabitacionId) || pricesByTypeMax.get(room.tipoHabitacionId)! < room.precioPorNoche) {
              pricesByTypeMax.set(room.tipoHabitacionId, room.precioPorNoche);
            }
          });

          this.roomTypeOptions = types.map(type => ({
            id: type.id,
            name: type.nombre,                          // Campo del OpenAPI
            priceMin: pricesByTypeMin.get(type.id) || 0,
            priceMax: pricesByTypeMax.get(type.id) || 0,
            precioPromedio: ((pricesByTypeMax.get(type.id) || 0) + (pricesByTypeMin.get(type.id) || 0)) / 2,
            capacidad: type.capacidad                   // Campo del OpenAPI (1-10)
          }));
        }
        // Seleccionar el primer tipo disponible por defecto
        this.selectDefaultRoomType();
      },
      error: (error) => {
        console.error('Error loading room types:', error);
        this.roomTypeOptions = [];
        this.selectDefaultRoomType();
      }
    });
  }

  /**
   * Obtiene los tipos de habitación filtrados por capacidad según los huéspedes seleccionados
   * Usa el campo 'capacidad' del OpenAPI (1-10 personas)
   */
  get filteredRoomTypes(): RoomTypeOption[] {
    const guests = parseInt(this.selectedGuests) || 1;
    return this.roomTypeOptions.filter(room => room.capacidad >= guests);
  }

  /**
   * Selecciona el primer tipo de habitación disponible
   */
  private selectDefaultRoomType(): void {
    const available = this.filteredRoomTypes;
    if (available.length > 0) {
      this.selectedRoomType = available[0].id.toString();
    } else {
      this.selectedRoomType = '';
    }
  }

  /**
   * Maneja el cambio en la cantidad de huéspedes
   * Filtra y actualiza los tipos de habitación disponibles
   */
  onGuestsChange(): void {
    const available = this.filteredRoomTypes;

    // Verificar si la habitación seleccionada sigue siendo válida
    const currentSelection = available.find(r => r.id.toString() === this.selectedRoomType);

    if (!currentSelection && available.length > 0) {
      // Si la selección actual ya no es válida, seleccionar la primera disponible
      this.selectedRoomType = available[0].id.toString();
    } else if (available.length === 0) {
      this.selectedRoomType = '';
    }
  }

  /**
   * Carga las habitaciones desde la API
   * Usa los endpoints /rooms y /room-types sin authorization header
   */
  private loadRooms(): void {
    this.isLoadingRooms = true;

    this.apiService.getRoomsForDisplay().subscribe({
      next: (rooms) => {
        if (rooms && rooms.length > 0) {
          this.rooms = rooms;
        } else {
          // Si no hay datos de la API, usar datos por defecto
          this.rooms = [];
        }
        this.isLoadingRooms = false;
        // Iniciar autoplay después de cargar las habitaciones
        this.startAutoplay();
      },
      error: (error) => {
        console.error('Error loading rooms:', error);
        // En caso de error, usar datos por defecto
        this.rooms = [];
        this.isLoadingRooms = false;
        // Iniciar autoplay con datos por defecto
        this.startAutoplay();
      }
    });
  }

  /**
   * Obtiene el nombre del tipo de habitación seleccionado
   */
  getSelectedRoomTypeName(): string {
    const selected = this.roomTypeOptions.find(r => r.id.toString() === this.selectedRoomType);
    return selected ? selected.name : 'Seleccione una habitación';
  }

  /**
   * Obtiene el precio del tipo de habitación seleccionado
   */
  getMaxSelectedRoomTypePrice(): number {
    const selected = this.roomTypeOptions.find(r => r.id.toString() === this.selectedRoomType);
    return selected ? selected.priceMax : 0;
  }

  /**
  * Obtiene el precio del tipo de habitación seleccionado
  */
  getMinSelectedRoomTypePrice(): number {
    const selected = this.roomTypeOptions.find(r => r.id.toString() === this.selectedRoomType);
    return selected ? selected.priceMin : 0;
  }

  /**
   * Obtiene la capacidad del tipo de habitación seleccionado
   * Campo 'capacidad' del OpenAPI
   */
  getSelectedRoomTypeCapacity(): number {
    const selected = this.roomTypeOptions.find(r => r.id.toString() === this.selectedRoomType);
    return selected ? selected.capacidad : 0;
  }

  /**
   * Verifica si hay habitaciones disponibles para la cantidad de huéspedes
   */
  hasAvailableRooms(): boolean {
    return this.filteredRoomTypes.length > 0;
  }

  /**
   * Calcula el precio estimado
   */
  calculateEstimatedPriceMin(): number {
    if (!this.checkInDate || !this.checkOutDate) return 0;

    const checkIn = new Date(this.checkInDate);
    const checkOut = new Date(this.checkOutDate);
    const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));

    if (nights <= 0) return 0;

    const pricePerNight = this.getMinSelectedRoomTypePrice();
    return pricePerNight * nights * this.selectedRoomQuantity;
  }

  /**
 * Calcula el precio estimado
 */
  calculateEstimatedPriceMax(): number {
    if (!this.checkInDate || !this.checkOutDate) return 0;

    const checkIn = new Date(this.checkInDate);
    const checkOut = new Date(this.checkOutDate);
    const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));

    if (nights <= 0) return 0;

    const pricePerNight = this.getMaxSelectedRoomTypePrice();
    return pricePerNight * nights * this.selectedRoomQuantity;
  }


  /**
   * Valida que la fecha de checkout sea posterior a checkin
   */
  onCheckInChange(): void {
    if (this.checkInDate && this.checkOutDate) {
      const checkIn = new Date(this.checkInDate);
      const checkOut = new Date(this.checkOutDate);

      if (checkOut <= checkIn) {
        const newCheckOut = new Date(checkIn);
        newCheckOut.setDate(newCheckOut.getDate() + 1);
        this.checkOutDate = this.formatDate(newCheckOut);
      }
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    this.checkScroll();
  }

  @HostListener('window:resize', [])
  onWindowResize(): void {
    this.updateCarouselSettings();
  }

  private checkScroll(): void {
    this.isScrolled = window.pageYOffset > 50;
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  scrollToSection(sectionId: string): void {
    this.mobileMenuOpen = false; // Close mobile menu on navigation
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for fixed navbar
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  checkAvailability(): void {
    // Validate form fields
    if (!this.checkInDate || !this.checkOutDate || !this.selectedGuests || !this.selectedRoomType) {
      alert('Por favor completa todos los campos antes de verificar disponibilidad');
      return;
    }

    // Validate dates
    const checkIn = new Date(this.checkInDate);
    const checkOut = new Date(this.checkOutDate);
    if (checkOut <= checkIn) {
      alert('La fecha de salida debe ser posterior a la fecha de entrada');
      return;
    }

    const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
    if (nights > 30) {
      alert('La estadía máxima permitida es de 30 días');
      return;
    }

    const adults = parseInt(this.selectedGuests) || 1;

    // Call API to check availability
    this.apiService.checkRoomAvailability(this.checkInDate, this.checkOutDate, adults).subscribe({
      next: (response) => {
        // Navigate to booking wizard with query params
        // Note: We'll pass the data via query params so the wizard can pre-fill step 1 and jump to step 2
        const queryParams = {
          checkIn: this.checkInDate,
          checkOut: this.checkOutDate,
          adults: adults.toString(),
          children: '0',
          step: '2' // Jump directly to room selection step
        };

        // Navigate to the booking wizard
        window.location.href = `/reservar?${new URLSearchParams(queryParams).toString()}`;
      },
      error: (error) => {
        console.error('Error checking availability:', error);
        const errorMessage = error?.error?.message || 'No se pudo verificar la disponibilidad. Por favor intenta nuevamente.';
        alert(errorMessage);
      }
    });
  }

  /**
   * Obtiene la fecha mínima para el input de fecha (hoy)
   */
  getMinDate(): string {
    return this.formatDate(new Date());
  }

  /**
   * Obtiene la fecha mínima para checkout (día después del checkin)
   */
  getMinCheckoutDate(): string {
    if (this.checkInDate) {
      const checkIn = new Date(this.checkInDate);
      checkIn.setDate(checkIn.getDate() + 1);
      return this.formatDate(checkIn);
    }
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return this.formatDate(tomorrow);
  }
}
